import React, { useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import "./ticketPanelConfig.css";

const FIELD_TYPES = [
  { value: "short", label: "Texto Curto" },
  { value: "long", label: "Texto Longo" },
  { value: "number", label: "Número" },
  { value: "date", label: "Data" },
];

const FIELD_VARIABLES = [
  { value: "none", label: "Corpo da Mensagem" },
  { value: "title", label: "Titulo" },
  { value: "subtitle", label: "Descrição do Titulo" },
];

const DEFAULT_OPTION_NAMES = { 1: "", 2: "", 3: "" };
const DEFAULT_STAGES = { 1: [], 2: [], 3: [] };
const DEFAULT_CUSTOM_FIELDS = [
  { id: "cf-1", question: "", type: "short", variable: "none" },
];

const allowedFieldTypes = new Set(
  FIELD_TYPES.map((f) => f.value)
);

const allowedFieldVariables = new Set(
  FIELD_VARIABLES.map((f) => f.value)
);

/** Variáveis Mustache usadas em `formatBodyPublicPanel` no envio WhatsApp do estágio */
const STAGE_MESSAGE_VARIABLES = [
  { label: "Nome do contato", token: "{{nome}}" },
  { label: "Código do chamado", token: "{{numero_ticket}}" },
  { label: "Primeiro nome", token: "{{firstName}}" },
  { label: "Nome", token: "{{name}}" },
  { label: "Protocolo", token: "{{protocol}}" },
  { label: "Assunto (subtítulo)", token: "{{assunto}}" },
  { label: "Título", token: "{{titulo}}" },
  { label: "Nome da empresa", token: "{{name_company}}" },
  { label: "Saudação", token: "{{ms}}" },
  { label: "Data", token: "{{date}}" },
  { label: "Hora", token: "{{hour}}" },
  { label: "Data e hora", token: "{{data_hora}}" },
];

const normalizeStage = (s) => ({
  id: String(s?.id || `stage-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`),
  name: String(s?.name ?? "Novo estágio").trim() || "Novo estágio",
  color: typeof s?.color === "string" && /^#[0-9a-fA-F]{6}$/.test(s.color)
    ? s.color
    : "#9ca3af",
  message: String(s?.message ?? ""),
});

const normalizeCustomField = (f, idx) => ({
  id: String(f?.id || `cf-${Date.now()}-${idx}`),
  question: String(f?.question ?? ""),
  type: allowedFieldTypes.has(f?.type) ? f.type : "short",
  variable: allowedFieldVariables.has(f?.variable) ? f.variable : "none",
});

const mergeLoadedConfig = (data) => {
  if (!data || typeof data !== "object") {
    return {
      colInputs: { ...DEFAULT_OPTION_NAMES },
      stages: { 1: [], 2: [], 3: [] },
      customFields: [...DEFAULT_CUSTOM_FIELDS],
      ticketMovementNoticePhone: "",
      ticketMovementNoticeMessage: "",
    };
  }
  const on = data.optionNames && typeof data.optionNames === "object"
    ? data.optionNames
    : {};
  const colInputs = {
    1: String(on["1"] ?? on[1] ?? ""),
    2: String(on["2"] ?? on[2] ?? ""),
    3: String(on["3"] ?? on[3] ?? ""),
  };
  const st = data.stages && typeof data.stages === "object" ? data.stages : {};
  const stages = {
    1: Array.isArray(st["1"]) ? st["1"].map(normalizeStage) : [],
    2: Array.isArray(st["2"]) ? st["2"].map(normalizeStage) : [],
    3: Array.isArray(st["3"]) ? st["3"].map(normalizeStage) : [],
  };
  const customFields = Array.isArray(data.customFields)
    ? data.customFields.map(normalizeCustomField)
    : [...DEFAULT_CUSTOM_FIELDS];
  const ticketMovementNoticePhone = String(
    data.ticketMovementNoticePhone ?? ""
  );
  const ticketMovementNoticeMessage = String(
    data.ticketMovementNoticeMessage ?? ""
  );
  return {
    colInputs,
    stages,
    customFields,
    ticketMovementNoticePhone,
    ticketMovementNoticeMessage,
  };
};

const TicketConfigurationPage = () => {
  const [colInputs, setColInputs] = useState({ ...DEFAULT_OPTION_NAMES });
  const [stages, setStages] = useState({ ...DEFAULT_STAGES });
  const [customFields, setCustomFields] = useState([...DEFAULT_CUSTOM_FIELDS]);
  const [movementNoticePhone, setMovementNoticePhone] = useState("");
  const [movementNoticeMessage, setMovementNoticeMessage] = useState("");
  const [loadState, setLoadState] = useState("idle");
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTarget, setModalTarget] = useState(null);
  const [modalDraft, setModalDraft] = useState("");
  const stageMessageTextareaRef = useRef(null);
  const movementNoticeTextareaRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadState("loading");
      try {
        const { data } = await api.get("/ticket-panel-config");
        if (cancelled) return;
        const merged = mergeLoadedConfig(data);
        setColInputs(merged.colInputs);
        setStages(merged.stages);
        setCustomFields(merged.customFields);
        setMovementNoticePhone(merged.ticketMovementNoticePhone);
        setMovementNoticeMessage(merged.ticketMovementNoticeMessage);
        setLoadState("ready");
      } catch (err) {
        if (!cancelled) {
          setLoadState("error");
          toastError(err);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const buildPayload = useCallback(() => {
    const optionNames = {
      "1": colInputs[1] ?? "",
      "2": colInputs[2] ?? "",
      "3": colInputs[3] ?? "",
    };
    const stagesPayload = {
      "1": stages[1],
      "2": stages[2],
      "3": stages[3],
    };
    return {
      optionNames,
      stages: stagesPayload,
      customFields,
      ticketMovementNoticePhone: movementNoticePhone,
      ticketMovementNoticeMessage: movementNoticeMessage,
    };
  }, [colInputs, stages, customFields, movementNoticePhone, movementNoticeMessage]);

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await api.put("/ticket-panel-config", buildPayload());
      toast.success("Configurações salvas com sucesso.");
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const colTitle = useCallback(
    (n) => {
      const t = (colInputs[n] || "").trim();
      return t || `Opção ${n}`;
    },
    [colInputs]
  );

  const updateColumnTitle = (n, value) => {
    setColInputs((prev) => ({ ...prev, [n]: value }));
  };

  const addStage = (column) => {
    const id = `stage-${column}-${Date.now()}`;
    setStages((s) => ({
      ...s,
      [column]: [
        ...s[column],
        {
          id,
          name: "Novo estágio",
          color: "#9ca3af",
          message: "",
        },
      ],
    }));
  };

  const removeStage = (column, stageId) => {
    setStages((s) => ({
      ...s,
      [column]: s[column].filter((st) => st.id !== stageId),
    }));
    if (modalTarget?.column === column && modalTarget?.stageId === stageId) {
      setModalOpen(false);
      setModalTarget(null);
      setModalDraft("");
    }
  };

  const updateStageName = (column, stageId, name) => {
    setStages((s) => ({
      ...s,
      [column]: s[column].map((st) =>
        st.id === stageId ? { ...st, name: name.trim() || "Novo estágio" } : st
      ),
    }));
  };

  const updateStageColor = (column, stageId, color) => {
    setStages((s) => ({
      ...s,
      [column]: s[column].map((st) =>
        st.id === stageId ? { ...st, color } : st
      ),
    }));
  };

  const openModal = (column, stageId) => {
    const st = stages[column].find((x) => x.id === stageId);
    setModalTarget({ column, stageId });
    setModalDraft(st?.message || "");
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalTarget(null);
    setModalDraft("");
  };

  const saveStageMessage = () => {
    if (!modalTarget) return;
    const { column, stageId } = modalTarget;
    setStages((s) => ({
      ...s,
      [column]: s[column].map((st) =>
        st.id === stageId ? { ...st, message: modalDraft } : st
      ),
    }));
    closeModal();
  };

  const insertStageVariable = (snippet) => {
    const el = stageMessageTextareaRef.current;
    if (!el) {
      setModalDraft((d) => `${d}${snippet}`);
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const base = el.value;
    const next = base.slice(0, start) + snippet + base.slice(end);
    setModalDraft(next);
    const pos = start + snippet.length;
    requestAnimationFrame(() => {
      const ta = stageMessageTextareaRef.current;
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(pos, pos);
    });
  };

  const insertMovementNoticeVariable = (snippet) => {
    const el = movementNoticeTextareaRef.current;
    if (!el) {
      setMovementNoticeMessage((d) => `${d}${snippet}`);
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const base = el.value;
    const next = base.slice(0, start) + snippet + base.slice(end);
    setMovementNoticeMessage(next);
    const pos = start + snippet.length;
    requestAnimationFrame(() => {
      const ta = movementNoticeTextareaRef.current;
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(pos, pos);
    });
  };

  const addCustomField = () => {
    setCustomFields((list) => [
      ...list,
      { id: `cf-${Date.now()}`, question: "", type: "short", variable: "none" },
    ]);
  };

  const removeCustomField = (id) => {
    setCustomFields((list) => list.filter((f) => f.id !== id));
  };

  const updateCustomField = (id, patch) => {
    setCustomFields((list) =>
      list.map((f) => (f.id === id ? { ...f, ...patch } : f))
    );
  };

  const setCustomFieldVariable = (id, nextVar) => {
    const v = String(nextVar || "none");
    if (v === "none") {
      updateCustomField(id, { variable: "none" });
      return;
    }
    if (v !== "title" && v !== "subtitle") {
      updateCustomField(id, { variable: "none" });
      return;
    }
    const conflict = customFields.find(
      (f) => f.id !== id && String(f.variable || "none") === v
    );
    if (conflict) {
      toast.warn(
        `A variável "${
          v === "title" ? "Titulo" : "Descrição do Titulo"
        }" já está em outra pergunta. Remova primeiro na pergunta anterior.`
      );
      return;
    }
    updateCustomField(id, { variable: v });
  };

  const modalBackdropClick = (e) => {
    if (e.target === e.currentTarget) closeModal();
  };

  const loadError = loadState === "error";
  const loadBusy = loadState === "loading" || loadState === "idle";

  return (
    <div className="ticket-panel-config-root">
      <section id="configuracoes" className="page active">
        <div className="config-save-bar">
          {loadError ? (
            <span className="save-status">
              Não foi possível carregar as configurações. Salvar pode sobrescrever
              dados no servidor.
            </span>
          ) : loadBusy ? (
            <span className="save-status">Carregando configurações…</span>
          ) : null}
          <button
            type="button"
            className="btn btn-primary btn-save-all"
            onClick={handleSaveAll}
            disabled={saving || loadBusy}
          >
            {saving ? "Salvando…" : "Salvar todas as configurações"}
          </button>
        </div>

        <div className="config-wrapper">
          <div className="columns">
            {[1, 2, 3].map((n) => (
              <div key={n} className="column" data-column={n}>
                <h3 id={`col-title-${n}`}>{colTitle(n)}</h3>
                <label htmlFor={`nome-opcao-${n}`}>
                  Nome personalizado da opção
                </label>
                <input
                  type="text"
                  id={`nome-opcao-${n}`}
                  placeholder={`Digite o nome da Opção ${n}`}
                  value={colInputs[n]}
                  onChange={(e) => updateColumnTitle(n, e.target.value)}
                />
                <div className="column-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => addStage(n)}
                  >
                    + Criar opção de estágio
                  </button>
                </div>
                <div className="stages" id={`stages-${n}`}>
                  {stages[n].map((st) => (
                    <div
                      key={st.id}
                      className="stage"
                      id={st.id}
                      style={{ borderLeftColor: st.color }}
                    >
                      <div className="stage-top">
                        <p className="stage-title">{st.name}</p>
                        <button
                          type="button"
                          className="delete-stage"
                          title="Excluir estágio"
                          onClick={() => removeStage(n, st.id)}
                        >
                          ×
                        </button>
                      </div>
                      <div className="stage-grid">
                        <div>
                          <label>Nome do estágio</label>
                          <input
                            type="text"
                            placeholder="Digite o nome do estágio"
                            value={st.name}
                            onChange={(e) =>
                              updateStageName(n, st.id, e.target.value)
                            }
                          />
                        </div>
                        <div className="color-wrap">
                          <label>Cor</label>
                          <input
                            type="color"
                            value={st.color}
                            onChange={(e) =>
                              updateStageColor(n, st.id, e.target.value)
                            }
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => openModal(n, st.id)}
                      >
                        Definir mensagem
                      </button>
                      <div className="message-preview" id={`preview-${st.id}`}>
                        <strong>Mensagem:</strong>{" "}
                        {st.message.trim()
                          ? st.message.split("\n").map((line, i) => (
                              <React.Fragment key={i}>
                                {i > 0 && <br />}
                                {line}
                              </React.Fragment>
                            ))
                          : "Nenhuma mensagem definida."}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="custom-fields-panel">
          <h3>Campos do Formulário de Abertura</h3>
          <p className="custom-fields-intro">
            Defina os campos que aparecerão na tela pública para o parceiro abrir
            o ticket.
          </p>

          <div id="custom-fields-list">
            {customFields.map((field) => (
              <div key={field.id} className="custom-field-item">
                <div className="custom-field-col">
                  <label htmlFor={`cf-question-${field.id}`}>Pergunta</label>
                  <input
                    id={`cf-question-${field.id}`}
                    type="text"
                    placeholder="Nome da pergunta (ex: Seu Email)"
                    value={field.question}
                    onChange={(e) =>
                      updateCustomField(field.id, { question: e.target.value })
                    }
                  />
                </div>

                <div className="custom-field-col">
                  <label htmlFor={`cf-variable-${field.id}`}>Variável</label>
                  <select
                    id={`cf-variable-${field.id}`}
                    value={field.variable || "none"}
                    onChange={(e) => setCustomFieldVariable(field.id, e.target.value)}
                  >
                    {FIELD_VARIABLES.map((opt) => {
                      const usedByOther = customFields.some(
                        (f) =>
                          f.id !== field.id &&
                          String(f.variable || "none") === opt.value &&
                          opt.value !== "none"
                      );
                      return (
                        <option
                          key={opt.value}
                          value={opt.value}
                          disabled={usedByOther}
                        >
                          {opt.label}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="custom-field-col">
                  <label htmlFor={`cf-type-${field.id}`}>Tipo</label>
                  <select
                    id={`cf-type-${field.id}`}
                    value={field.type}
                    onChange={(e) =>
                      updateCustomField(field.id, { type: e.target.value })
                    }
                  >
                    {FIELD_TYPES.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  className="delete-stage"
                  title="Excluir"
                  onClick={() => removeCustomField(field.id)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          <button
            type="button"
            className="btn btn-secondary btn-add-field"
            onClick={addCustomField}
          >
            + Adicionar Nova Pergunta
          </button>
        </div>

        <div className="custom-fields-panel movement-notice-panel">
          <h3>Aviso de movimentação de ticket</h3>
          <p className="custom-fields-intro">
            Número que receberá aviso quando um ticket do painel público for
            movimentado entre estágios, quando o cliente enviar mensagem no
            portal ou quando houver nova mensagem no chat do painel. Use o
            modelo abaixo com as variáveis disponíveis.
          </p>
          <div className="movement-notice-grid">
            <div>
              <label htmlFor="movement-notice-phone">Telefone (WhatsApp)</label>
              <input
                id="movement-notice-phone"
                type="text"
                inputMode="tel"
                autoComplete="tel"
                placeholder="Ex: 5511999998888"
                value={movementNoticePhone}
                onChange={(e) => setMovementNoticePhone(e.target.value)}
              />
            </div>
            <div className="movement-notice-message-block">
              <label htmlFor="movement-notice-message">
                Modelo de mensagem
              </label>
              <textarea
                ref={movementNoticeTextareaRef}
                id="movement-notice-message"
                rows={6}
                placeholder="Digite o texto do aviso…"
                value={movementNoticeMessage}
                onChange={(e) => setMovementNoticeMessage(e.target.value)}
              />
              <div className="stage-var-toolbar">
                <span className="stage-var-toolbar-title">Inserir variável</span>
                <div
                  className="stage-var-chips"
                  role="group"
                  aria-label="Variáveis do modelo de aviso"
                >
                  {STAGE_MESSAGE_VARIABLES.map((v) => (
                    <button
                      key={v.token}
                      type="button"
                      className="stage-var-chip"
                      title={`Insere ${v.token}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => insertMovementNoticeVariable(v.token)}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {createPortal(
        <div
          className={`tpc-modal-portal${modalOpen ? " show" : ""}`}
          onClick={modalBackdropClick}
          role="presentation"
        >
          <div className="modal-content" role="dialog" aria-modal="true">
            <div className="modal-header">
              <h3>Definir mensagem do estágio</h3>
            </div>
            <div className="modal-body">
              <label htmlFor="stageMessage">
                Mensagem enviada aos contatos quando o ticket chegar neste estágio
              </label>
              <textarea
                ref={stageMessageTextareaRef}
                id="stageMessage"
                rows={7}
                placeholder="Digite a mensagem deste estágio..."
                value={modalDraft}
                onChange={(e) => setModalDraft(e.target.value)}
              />
              <div className="stage-var-toolbar">
                <span className="stage-var-toolbar-title">Inserir variável</span>
                <div className="stage-var-chips" role="group" aria-label="Variáveis da mensagem">
                  {STAGE_MESSAGE_VARIABLES.map((v) => (
                    <button
                      key={v.token}
                      type="button"
                      className="stage-var-chip"
                      title={`Insere ${v.token}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => insertStageVariable(v.token)}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeModal}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={saveStageMessage}
              >
                Salvar mensagem
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default TicketConfigurationPage;
